import { useCallback, useMemo } from "react";
import { useTable } from "@refinedev/antd";
import { useInvalidate } from "@refinedev/core";
import { MediaProjectListItem } from "../../types";
import {
  useOrganizationSummaries,
  ProjectSessionSummary,
} from "../../home/hooks/useOrganizationAnalytics";
import {
  WSEvent,
  NOTIFICATIONS_EVENT_TYPE,
} from "../../../../store/websocketStore";
import { useWsSubscription } from "hooks/useWsSubscription";

export const EXPERIENCES_PAGE_SIZE = 20;

/**
 * Powers the org-level Experiences list. Joins two tiers:
 *  - DB (regular project endpoint): name, status, tags — drives pagination,
 *    search and sorting via Refine's useTable.
 *  - Batch analytics (POST /organizations/{id}/summaries): per-project views,
 *    read depth, chat — scoped to the ids on the current page so the batch
 *    call never asks for more than one page of projects.
 *
 * The summaries are exposed as a Map keyed by project_id (mirrors the
 * summariesMap join in projects/index.tsx) plus the raw project ids of the
 * current page.
 */
export function useExperiencesData(
  organizationId: string | undefined,
  workspaceId?: string
) {
  const resource = useMemo(() => {
    const base = `media/${organizationId}/projects`;
    return workspaceId ? `${base}?workspace_id=${workspaceId}` : base;
  }, [organizationId, workspaceId]);
  const invalidate = useInvalidate();

  const {
    tableQuery: { isLoading: isLoadingProjects, data: projectsData },
    tableProps,
    setCurrent,
    setFilters,
    setSorters,
  } = useTable<MediaProjectListItem>({
    resource,
    queryOptions: {
      enabled: Boolean(organizationId),
      refetchOnMount: "always",
    },
    pagination: {
      pageSize: EXPERIENCES_PAGE_SIZE,
    },
    sorters: {
      initial: [{ field: "created", order: "desc" }],
    },
  });

  const projects = useMemo(() => projectsData?.data ?? [], [projectsData]);

  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);

  // Scope the batch call to the ids currently on the page. project_ids is sent
  // in the body; page/size are query params (BE caps size at 50).
  const { summaries, isLoading: isLoadingPageSummaries } =
    useOrganizationSummaries(organizationId, {
      page: 1,
      size: EXPERIENCES_PAGE_SIZE,
      projectIds,
      enabled: projectIds.length > 0,
    });
  // The summaries request waits for the project list, so it is loading too.
  const isLoadingSummaries = isLoadingProjects || isLoadingPageSummaries;

  const summariesMap = useMemo(() => {
    const map = new Map<string, ProjectSessionSummary>();
    summaries?.forEach((s) => map.set(s.project_id, s));
    return map;
  }, [summaries]);

  // Live publication-status pills: refetch the DB list when a status changes,
  // reusing the same per-project subscription (no polling).
  const handleStatusChange = useCallback(
    (event: WSEvent) => {
      if (event.event !== NOTIFICATIONS_EVENT_TYPE.ProjectStatusChanged) return;
      invalidate({ resource, invalidates: ["list"] }).catch((error) => {
        console.error("Failed to invalidate experiences list:", error);
      });
    },
    [invalidate, resource]
  );

  const statusSubscriptionConfigs = useMemo(
    () => projectIds.map((id) => ({ type: "project" as const, id })),
    [projectIds]
  );

  useWsSubscription(statusSubscriptionConfigs, handleStatusChange);

  return {
    projects,
    projectIds,
    summariesMap,
    tableProps,
    setCurrent,
    setFilters,
    setSorters,
    isLoading: isLoadingProjects,
    isLoadingSummaries,
  };
}
